/**
 * KPI Section Component
 *
 * Displays main KPI metrics and secondary KPI cards
 */

import { clientConfig } from '@/config/clientConfig';
import type { DashboardKpisResponse } from '@skewb-works/contracts';
import { AlertTriangle, Briefcase, CheckCircle2, PoundSterling } from 'lucide-react';
import { KpiCard } from './ChartComponents';

interface KpiSectionProps {
  data: DashboardKpisResponse;
}

/**
 * Each tile spans 2 columns, so the widest breakpoint needs twice as many
 * columns as there are visible tiles — otherwise hiding a tile leaves dead
 * space to the right of the row. Spelled out in full because Tailwind only
 * emits classes it can find as literal strings.
 */
const XL_GRID_COLS: Record<number, string> = {
  2: 'xl:grid-cols-4',
  3: 'xl:grid-cols-6',
  4: 'xl:grid-cols-8',
};

export default function KpiSection({ data }: KpiSectionProps) {
  const { totals, deltas } = data;

  // SC-6603: the construction-related tiles are hidden for brands that don't
  // run that workflow (e.g. Yorkshire Water), and kept for everyone else.
  const showValueKpi = clientConfig.features.dashboardValueKpi;
  const showRiskKpi = clientConfig.features.dashboardRiskKpi;
  const visibleTiles = 2 + (showValueKpi ? 1 : 0) + (showRiskKpi ? 1 : 0);

  return (
    <>
      {/* Main KPI row */}
      <div className={`grid grid-cols-2 lg:grid-cols-4 ${XL_GRID_COLS[visibleTiles]} gap-4`}>
        <div className="col-span-2">
          <KpiCard
            label="Total Jobs"
            value={String(totals.totalJobs)}
            sub="All statuses"
            icon={Briefcase}
            accent="bg-blue-600"
            trend={
              deltas?.totalJobsVsPrevPct
                ? `${deltas.totalJobsVsPrevPct > 0 ? '+' : ''}${deltas.totalJobsVsPrevPct}% vs last period`
                : undefined
            }
            trendUp={deltas?.totalJobsVsPrevPct ? deltas.totalJobsVsPrevPct > 0 : undefined}
          />
        </div>
        <div className="col-span-2">
          <KpiCard
            label="Completed"
            value={String(totals.completed)}
            sub={`${totals.completionRatePct}% completion rate`}
            icon={CheckCircle2}
            accent="bg-green-600"
            trend={
              deltas?.completedVsPrevPct
                ? `${deltas.completedVsPrevPct > 0 ? '+' : ''}${deltas.completedVsPrevPct}% vs last period`
                : undefined
            }
            trendUp={deltas?.completedVsPrevPct ? deltas.completedVsPrevPct > 0 : undefined}
          />
        </div>
        {showValueKpi && (
          <div className="col-span-2">
            <KpiCard
              label="Total Value"
              value={`£${(totals.totalValue / 1000).toFixed(0)}k`}
              sub={`Avg £${totals.avgValuePerJob.toLocaleString()} per job`}
              icon={PoundSterling}
              accent="bg-orange-600"
              trend={
                deltas?.totalValueVsPrev
                  ? `${deltas.totalValueVsPrev > 0 ? '+' : ''}£${Math.abs(deltas.totalValueVsPrev / 1000).toFixed(0)}k vs last period`
                  : undefined
              }
              trendUp={deltas?.totalValueVsPrev ? deltas.totalValueVsPrev > 0 : undefined}
            />
          </div>
        )}
        {showRiskKpi && (
          <div className="col-span-2">
            <KpiCard
              label="Urgent / At Risk"
              value={String(totals.urgent)}
              sub={`${totals.aborted} aborted this period`}
              icon={AlertTriangle}
              accent="bg-red-600"
              trend={
                deltas?.urgentVsPrev
                  ? `${deltas.urgentVsPrev > 0 ? '+' : ''}${deltas.urgentVsPrev} vs last period`
                  : undefined
              }
              trendUp={deltas?.urgentVsPrev ? deltas.urgentVsPrev < 0 : undefined}
            />
          </div>
        )}
      </div>

      {/* Secondary KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
          <div className="text-xs text-muted-foreground mb-1">In Progress</div>
          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {totals.inProgress}
          </div>
          <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full"
              style={{
                width: `${totals.totalJobs > 0 ? (totals.inProgress / totals.totalJobs) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
          <div className="text-xs text-muted-foreground mb-1">Not Started</div>
          <div className="text-2xl font-bold text-slate-600 dark:text-slate-300">
            {totals.pending}
          </div>
          <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-slate-400 rounded-full"
              style={{
                width: `${totals.totalJobs > 0 ? (totals.pending / totals.totalJobs) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 shadow-sm">
          <div className="text-xs text-muted-foreground mb-1">% Complete</div>
          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
            {totals.completionRatePct}%
          </div>
          <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full"
              style={{ width: `${totals.completionRatePct}%` }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
